import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Divider, Pagination, Popover, Stack, TextField, Typography, useTheme } from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

import { ApiPagination, Tag } from "./TagManagementPage";
import Chip from "components/uiElements/dataDisplay/Chip";
import APIRequest, { APIError } from "system/APIRequest";
import TagDialog from "./TagDialog";
import { useTranslation } from "react-i18next";

interface TagSelectorProps {
  disabled?: boolean;
  label?: string;
  onClose(selectedTags: Array<Tag>): void;
  selectedTags?: Array<Tag>;
  tags?: Array<Tag>;
}

const PaginationStart: ApiPagination = {
  cursor: "",
  page: 1,
  per_page: 10,
  total: 0
};

export default function TagSelector(props: TagSelectorProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const initialSelectedTags = (props.selectedTags || []);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const [allTags, setAllTags] = useState<Array<Tag>>([]);
  const [selectedTags, setSelectedTags] = useState<Array<Tag>>(initialSelectedTags);
  const [pagination, setPagination] = useState<ApiPagination>({...PaginationStart});

  const newTag = useRef<Tag | null>(null);
  const [showTagDialog, setShowTagDialog] = useState<boolean>(false);

  const tags = props.tags || allTags;

  async function loadAllTags() {
    try {
      const queryParams = {
        per_page: 1000+"",
        sort: "use_count",
        sort_order: "desc"
      };
      const response: any = await APIRequest("obj/resource-tag", queryParams);
      setAllTags(response.output);
      setSelectedTags(response.output.filter((allTag: Tag) => {
        if (newTag.current?.id === allTag.id) {
          return true;
        }
        return !!selectedTags.find((selectedTag) => selectedTag.id === allTag.id);
      }));
      setPagination({...pagination, total: response.pagination.total});
      newTag.current = null;
    } catch (e) {
      const error = e as APIError;
      // TODO
    } finally {
      
    }
  }

  useEffect(() => {
    loadAllTags();
  }, []);

  const filteredTags = useMemo(() => {
    if (inputValue.length === 0) {
      setPagination({...pagination, total: tags.length});
      return tags;
    }
    const result = tags.filter((tag) => {
      return tag.name.toLowerCase().includes(inputValue.toLowerCase());
    });
    setPagination({...pagination, total: result.length});
    return result;
  }, [tags, inputValue]);

  const sortedTags = useMemo(() => {
    const sortedTags = filteredTags.sort((a: Tag, b: Tag) => {
      const aIsSelected = !!selectedTags.find((tag) => tag.id === a.id);
      const bIsSelected = !!selectedTags.find((tag) => tag.id === b.id);
      if (aIsSelected && !bIsSelected) return -1;
      if (!aIsSelected && bIsSelected) return 1;
      if ((a.use_count || 0) > (b.use_count || 0)) return -1;
      if ((a.use_count || 0) < (b.use_count || 0)) return 1;
      if (a.name > b.name) return 1;
      if (a.name < b.name) return -1;
      return 0;
    });
    const start = (pagination.page-1) * pagination.per_page;
    const end = Math.min(start + pagination.per_page, pagination.total);
    return sortedTags.slice(start, end);
  }, [filteredTags, selectedTags, pagination.page, pagination.total]);

  function handleOpen(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget)
    // Reset the user's selection if the Popover is re-opened
    setInputValue("");
    newTag.current = null;
    setSelectedTags(initialSelectedTags);
  }

  function handleClose() {
    setAnchorEl(null);
    props.onClose(selectedTags);
  }

  function handleTagClicked(tag: Tag) {
    const found = selectedTags.find((selectedTag) => selectedTag.id === tag.id);
    if (!found) {
      setSelectedTags([...selectedTags, tag]);
    } else {
      const result = selectedTags.filter((selectedTag) => selectedTag.id !== tag.id);
      setSelectedTags(result);
    }
  }

  function handleCreateTag() {
    setShowTagDialog(true);
    newTag.current = {
      id: "",
      name: inputValue
    };
  }

  function handleCreateTagClose(tagId: string) {
    setInputValue("");
    setShowTagDialog(false);
    if (!tagId) {
      return;
    }
    newTag.current = {
      id: tagId,
      name: newTag.current?.name ?? ""
    };
    if (tagId) {
      loadAllTags();
    }
  }

  function handlePageChanged(event: ChangeEvent<unknown>, value: number): void {
    setPagination({...pagination, page: value});
  }

  const inputMatchesSelectedTag = tags.find((tag) => {
    return inputValue.toLowerCase() === tag.name.toLowerCase();
  });

  const isValidNewTagName = inputValue.length > 0 && !inputMatchesSelectedTag;
  const label = props.label || i18n.t("tag-selector.button-label");
  const open = Boolean(anchorEl);
  const pageCount = Math.ceil(pagination.total/pagination.per_page);

  return (
    <>
      <Button
        color="success"
        disabled={props.disabled || false}
        onClick={(event) => handleOpen(event)}
        size="small"
        variant="outlined"
      >
        <Box sx={{
          whiteSpace: "nowrap"
        }}>{label}</Box>
        <ArrowDropDownIcon/>
      </Button>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom"
        }}
        onClose={() => handleClose()}
        open={open}
        slotProps={{
          paper: {
            sx: {width: 250}
          }
        }}
      >
        <Stack divider={<Divider />}>
          <Typography variant="h6" sx={{
            padding: 1
          }}>{i18n.t("tag-selector.selector-header")}</Typography>
          <Box sx={{
            padding: 1
          }}>
            <TextField
              autoComplete="off"
              fullWidth
              onChange={(event) => setInputValue(event.target.value)}
              placeholder={i18n.t("tag-selector.search-placeholder")}
              size="small"
              value={inputValue}
            />
          </Box>
          <Stack
            divider={<Divider />}
            sx={{
              maxHeight: 200,
              overflow: "auto",
              cursor: "pointer"
            }}>
            {sortedTags.map((tag, index) => {
              const isSelected = selectedTags.find((selectedTag) => selectedTag.id === tag.id);
              return (
                <Box key={`TagSelector.Tag.${tag.id}`} onClick={() => handleTagClicked(tag)} >
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: "center",
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      "&:hover": { backgroundColor: theme.palette.action.hover }
                    }}>
                    <CheckIcon fontSize="small" sx={{visibility: isSelected ? "visible" : "hidden"}} />
                    <Box sx={{
                      flex: 1
                    }}>
                      <Chip color={tag.color} label={tag.name} />
                    </Box>
                    <CloseIcon fontSize="small" sx={{color: theme.palette.grey[500], visibility: isSelected ? "visible" : "hidden"}} />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
          {pageCount > 1 &&
            <Pagination
              boundaryCount={0}
              count={pageCount}
              onChange={handlePageChanged}
              page={pagination.page}
              shape="rounded"
              showFirstButton
              showLastButton
              siblingCount={0}
              size="small"
              sx={{ alignSelf: "center", paddingBottom: 0.5, paddingTop: 0.5 }}
            />
          }
          {isValidNewTagName &&
            <Box
              onClick={handleCreateTag}
              sx={{
                padding: 1,
                cursor: "pointer",
                "&:hover": { backgroundColor: theme.palette.action.hover }
              }}>
              {i18n.t("tag-selector.search-create-new", {inputValue: inputValue})}
            </Box>
          }
        </Stack>
      </Popover>
      {showTagDialog &&
        <TagDialog onClose={handleCreateTagClose} tag={newTag.current} />
      }
    </>
  );
}